import { type InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import {
	MessageStatus,
	type PostStaffMessageRequest,
	type StaffCommunicationHistoryResponse,
	type StaffCommunicationSummaryResponse,
	type UserSentMessage,
} from "../../../utils/models";

const getStaffCommunicationThreadsQueryKey = (departmentId: string) => ["staffCommunicationThreads", departmentId];
const getStaffCommunicationThreadQueryKey = (departmentId: string, userId: string) => ["staffCommunicationThread", departmentId, userId];
const THREAD_HISTORY_PAGE_SIZE = 100;
const STAFF_COMMUNICATION_REFETCH_INTERVAL_MS = 120_000;

type StaffCommunicationSummaryQueryData = StaffCommunicationSummaryResponse;

export const useThreadsQuery = (departmentId: string) => {
	return useQuery<StaffCommunicationSummaryQueryData>({
		queryKey: getStaffCommunicationThreadsQueryKey(departmentId),
		queryFn: async () => {
			const response = await backendService.fetchStaffCommunicationThreadSummaries(departmentId);

			// Convert date strings to luxon objects
			const smsWithDates = response.data.sms.map((msg: UserSentMessage) => ({
				...msg,
				dateCreated: msg.dateCreated,
			}));

			const pushWithDates = response.data.push.map((msg) => ({
				...msg,
				dateSent: msg.dateSent,
			}));

			return {
				sms: smsWithDates,
				push: pushWithDates,
				unreadCounts: response.data.unreadCounts,
			};
		},
		refetchInterval: STAFF_COMMUNICATION_REFETCH_INTERVAL_MS,
		throwOnError: (error: unknown) => {
			console.warn("Staff communication threads fetch failed (suppressed toast):", error);
			return false;
		},
		gcTime: 0, // replaces keepPreviousData: false
		enabled: !!departmentId, // Prevents query if no ID is provided
	});
};

export const useThreadMessagesQuery = (departmentId: string, userId: string | undefined) => {
	return useInfiniteQuery({
		queryKey: getStaffCommunicationThreadQueryKey(departmentId, userId ?? ""), // DEFAULT: disabled queries need a stable key before a nurse is selected.
		queryFn: async ({ pageParam }) => {
			if (!userId) throw new Error("A user must be selected before loading message history");
			const response = await backendService.fetchStaffCommunicationThreadHistory(departmentId, userId, THREAD_HISTORY_PAGE_SIZE, pageParam);
			return response.data;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length * THREAD_HISTORY_PAGE_SIZE : undefined),
		refetchInterval: STAFF_COMMUNICATION_REFETCH_INTERVAL_MS,
		enabled: Boolean(departmentId && userId),
		throwOnError: (error: unknown) => {
			console.warn("Staff communication thread messages fetch failed (suppressed toast):", error);
			return false;
		},
	});
};

export const usePostStaffMessage = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const {
		hospitalData,
		showErrorSnackbar,
		getNurseFormattedName,
		chosenDepartment: { department_id: chosenDepartmentId },
	} = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();

	return useMutation({
		mutationFn: async ({ message, userIds, addAppLink }: PostStaffMessageRequest) => {
			const res = await backendService.postStaffCommunicationMessage(chosenDepartmentId, userIds, message, addAppLink);
			await new Promise((resolve) => setTimeout(resolve, 7_000));
			const sids = res.data.flatMap((sentMessage) => (sentMessage.success && sentMessage.sid ? [sentMessage.sid] : []));
			// DEFAULT: a failed, non-critical delivery-status refresh must not turn a successful send into a failed send.
			let statusMessages: UserSentMessage[] = [];
			try {
				statusMessages = (await backendService.getMessagesStatuses(chosenDepartmentId, sids)).data;
			} catch (error) {
				console.warn("Staff communication status refresh failed (suppressed toast):", error);
			}
			return { res, statusMessages };
		},
		throwOnError: false,
		onMutate: async ({ userIds, message, tempId }) => {
			await queryClient.cancelQueries({
				queryKey: getStaffCommunicationThreadsQueryKey(chosenDepartmentId),
			});

			const previousThreads = queryClient.getQueryData<StaffCommunicationSummaryQueryData>(getStaffCommunicationThreadsQueryKey(chosenDepartmentId));

			userIds.forEach((userId) => {
				const newMessage: UserSentMessage = {
					body: message,
					dateCreated: DateTime.now(),
					tempId,
					departmentId: chosenDepartmentId,
					userId,
					senderUserId: userInfo.id,
					id: 0,
					status: MessageStatus.sending,
					direction: "outbound",
					senderFirstName: userInfo.first_name,
				};

				queryClient.setQueryData<StaffCommunicationSummaryQueryData>(getStaffCommunicationThreadsQueryKey(chosenDepartmentId), (old) =>
					old ? { ...old, sms: [newMessage, ...old.sms.filter((item) => item.userId !== userId)] } : old,
				);
				queryClient.setQueryData<InfiniteData<StaffCommunicationHistoryResponse>>(getStaffCommunicationThreadQueryKey(chosenDepartmentId, userId), (old) => {
					if (!old?.pages[0]) return old;
					const [firstPage, ...remainingPages] = old.pages;
					return { ...old, pages: [{ ...firstPage, sms: [newMessage, ...firstPage.sms] }, ...remainingPages] };
				});
			});

			return { previousThreads, userIds, tempId };
		},
		onError: (error, _variables, context) => {
			queryClient.setQueryData(getStaffCommunicationThreadsQueryKey(chosenDepartmentId), context?.previousThreads);
			void queryClient.invalidateQueries({ queryKey: ["staffCommunicationThread", chosenDepartmentId] });
			showErrorSnackbar("Failed to post message", { error });
		},
		onSuccess: async ({ res, statusMessages }, { userIds }) => {
			if (res.data.filter((m) => m.success).length === 0) {
				showErrorSnackbar("Failed to send message", { userIds });
			}
			const failedMessages = statusMessages.filter((msg) => msg.status === MessageStatus.failed);
			void queryClient.invalidateQueries({ queryKey: getStaffCommunicationThreadsQueryKey(chosenDepartmentId) });
			void queryClient.invalidateQueries({ queryKey: ["staffCommunicationThread", chosenDepartmentId] });

			if (userIds.length > 1 /* Means we are broadcasting */ && failedMessages.length) {
				const failedUsers = failedMessages.map((m) => m.userId);
				const failedUserNames = hospitalData.nurses.filter(({ userId }) => userId && failedUsers.includes(userId)).map((n) => getNurseFormattedName(n.id));

				showSnackbar(t("messages.send.failed", { failedUsers: failedUserNames.join(", ") }), "warning");
			}
		},
	});
};

export const useUpdateThreadReadStatus = (departmentId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (threadUserId: string) => {
			const response = await backendService.updateThreadReadStatus(departmentId, threadUserId);
			return response.data;
		},
		onSuccess: (_, threadUserId) => {
			// Optimistically update the cache
			queryClient.setQueryData<StaffCommunicationSummaryQueryData>(getStaffCommunicationThreadsQueryKey(departmentId), (old) => {
				if (!old) return old;
				return {
					...old,
					unreadCounts: {
						...old.unreadCounts,
						[threadUserId]: 0,
					},
				};
			});
		},
		throwOnError: false,
	});
};
